import React from 'react';
import { Grid, Typography, Box } from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import Divider from '@mui/material/Divider';
import LoadingButtonGeneral from '../general/buttons/loadingButtonGeneral';

type Props = {
    title: string;
    labelSave?: string;
    labelBack?: string;
    variantSave?: 'text' | 'contained' | 'outlined';
    variantBack?: 'text' | 'contained' | 'outlined';
    iconSave?: JSX.Element | string | undefined;
    iconBack?: JSX.Element | string | undefined;
    iconTitle?: string | undefined;
    onClickSave?: () => void;
    onClickBack?: () => void;
    disabledSave?: boolean;
    disabledBack?: boolean;
    children?: any;
    additionalButtons?: React.ReactNode[] | React.ReactNode;
};

const FormView = ({
    title,
    labelSave = 'Guardar',
    labelBack = 'Volver',
    variantSave = 'contained',
    variantBack = 'contained',
    iconSave,
    iconBack,
    iconTitle,
    onClickSave,
    onClickBack,
    disabledSave,
    disabledBack,
    children,
    additionalButtons,
}: Props) => {
    const contrastStyleBackground = (
        variant: 'text' | 'contained' | 'outlined'
    ) => (variant === 'contained' ? 'primary.main' : undefined);

    const contrastStyleIcon = (variant: 'text' | 'contained' | 'outlined') =>
        variant === 'contained' ? 'primary.contrastText' : undefined;

    return (
        <Box px={2}>
            <Grid container display="flex" alignItems="center" mb={1}>
                <Grid item xs={12} sm={6}>
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                        }}
                    >
                        {iconTitle && (
                            <Box
                                component="img"
                                src={iconTitle}
                                sx={{
                                    width: '15%',
                                    marginRight: 2,
                                }}
                                alt="logo"
                            />
                        )}
                        <Typography
                            component="h3"
                            sx={{
                                flexGrow: 1,
                                color: 'primary.main',
                                fontSize: { lg: 24, md: 22, xs: 20 },
                            }}
                        >
                            {title}
                        </Typography>
                    </Box>
                </Grid>
                <Grid
                    item
                    xs={12}
                    sm={6}
                    sx={{
                        textAlign: {
                            xs: 'center',
                            sm: 'right',
                        },
                    }}
                >
                    {onClickSave && (
                        <LoadingButtonGeneral
                            onClick={onClickSave}
                            variant={variantSave}
                            startIcon={
                                iconSave || (
                                    <SaveIcon
                                        sx={{
                                            color: contrastStyleIcon(
                                                variantSave
                                            ),
                                            marginRight: {
                                                xs: -1,
                                                lg: undefined,
                                            },
                                        }}
                                    />
                                )
                            }
                            disabled={disabledBack}
                            title={labelSave}
                            sx={{
                                mr: 1,
                                fontSize: { lg: 12, md: 10.5, xs: 9 },
                                backgroundColor:
                                    contrastStyleBackground(variantSave),
                                minWidth: { xs: 0, lg: undefined },
                            }}
                            textSx={{
                                display: {
                                    xs: 'none',
                                    lg: 'block',
                                },
                            }}
                        />
                    )}
                    {onClickBack && (
                        <LoadingButtonGeneral
                            onClick={onClickBack}
                            variant={variantBack}
                            startIcon={
                                iconBack || (
                                    <ArrowBackIcon
                                        sx={{
                                            color: contrastStyleIcon(
                                                variantBack
                                            ),
                                            marginRight: {
                                                xs: -1,
                                                lg: undefined,
                                            },
                                        }}
                                    />
                                )
                            }
                            disabled={disabledSave}
                            title={labelBack}
                            sx={{
                                fontSize: { lg: 12, md: 10.5, xs: 9 },
                                backgroundColor:
                                    contrastStyleBackground(variantBack),
                                minWidth: { xs: 0, lg: undefined },
                            }}
                            textSx={{
                                display: {
                                    xs: 'none',
                                    lg: 'block',
                                },
                            }}
                        />
                    )}
                    {additionalButtons}
                </Grid>
            </Grid>
            <Divider />
            {children}
        </Box>
    );
};

export default FormView;
